import React, { useEffect, useState, useCallback } from "react";
import { Loader2, Upload, CheckCircle2, AlertTriangle, Mail, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { api, formatDateTime } from "@/lib/api";
import { LinkBox, ClearedList } from "./PhonePeLink";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const err = (e) => toast.error(e.response?.data?.detail || "Action failed");

const Section = ({ title, tone, items, empty, testid, children }) => {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, 10);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid={testid}>
      <p className={`font-semibold flex items-center gap-2 ${tone}`}>{items.length ? <AlertTriangle className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4 text-green-600" />} {title} ({items.length})</p>
      {items.length === 0 ? <p className="text-xs text-slate-500 mt-1">{empty}</p> : <div className="mt-2 space-y-2">{shown.map(children)}</div>}
      {items.length > 10 && <button type="button" onClick={() => setAll((v) => !v)} className="text-xs text-blue-700 hover:underline mt-2" data-testid={`${testid}-toggle`}>{all ? "Show fewer" : `Show all ${items.length}`}</button>}
    </div>
  );
};

const Row = ({ x, children, testid }) => (
  <div className="rounded-lg border border-slate-100 bg-slate-50 p-2 text-sm flex items-center justify-between gap-2 flex-wrap" data-testid={testid}>
    <div className="min-w-0">
      <p className="font-medium text-slate-800">{inr(x.amount)} · {x.name}</p>
      <p className="text-xs text-slate-500">{formatDateTime(x.at)} · UTR {x.utr}{x.reason ? ` · ${x.reason}` : ""}</p>
    </div>
    {children}
  </div>
);

const PastSummary = () => {
  const [day, setDay] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    if (!day) { toast.error("Pick a past date"); return; }
    if (!window.confirm(`Email the money summary for ${day} now?`)) return;
    setBusy(true);
    try {
      const { data: r } = await api.post("/fees/daily-summary/preview", { date: day });
      toast.success(r.sent ? `${day} summary (${inr(r.total)}) emailed to ${r.to}` : `${day} summary logged (email is in TEST mode)`);
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <span className="flex items-center gap-1">
      <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} className="h-8 w-40" data-testid="phonepe-past-summary-date" />
      <Button size="sm" variant="outline" onClick={go} disabled={busy} data-testid="phonepe-past-summary-send"><Mail className="h-4 w-4 mr-1" /> Send that day's summary</Button>
    </span>
  );
};

const UploadBox = ({ onDone }) => {
  const [file, setFile] = useState(null);
  const [pw, setPw] = useState("");
  const [tz, setTz] = useState("uk");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const go = async () => {
    if (!file) { toast.error("Choose the PhonePe statement PDF"); return; }
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("password", pw); fd.append("tz", tz);
      const { data } = await api.post("/fees/phonepe/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRes(data); toast.success(`Read ${data.credits} credits · ${data.auto_approved} auto-approved`); onDone();
    } catch (e) { err(e); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 space-y-3" data-testid="phonepe-upload">
      <p className="font-semibold text-slate-900">Upload PhonePe statement (PDF)</p>
      <p className="text-xs text-slate-500">Matches every UPI credit to the teachers' entries by UTR. A clean single match is approved automatically. Shared screenshots, several students on one payment or any amount difference always wait for you. Uploading the same dates again is safe.</p>
      <div className="flex flex-wrap gap-2 items-center">
        <Input type="file" accept="application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} className="max-w-xs" data-testid="phonepe-file" />
        <Input type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="PDF password (if any)" className="max-w-[200px]" data-testid="phonepe-password" />
        <select value={tz} onChange={(e) => setTz(e.target.value)} className="h-10 rounded-md border border-slate-200 px-2 text-sm" data-testid="phonepe-tz">
          <option value="uk">Times in this PDF are UK time</option>
          <option value="india">Times in this PDF are India time</option>
        </select>
        <Button onClick={go} disabled={busy} className="bg-blue-900 hover:bg-blue-950" data-testid="phonepe-upload-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4 mr-1" /> Check statement</>}</Button>
      </div>
      {res && <p className="text-sm text-slate-700" data-testid="phonepe-upload-result">{res.period.from} to {res.period.to}: {res.credits} credits ({res.new} new) · <b className="text-green-700">{res.auto_approved} auto-approved</b> · {res.needs_approval} need your approval · {res.mismatch} amount differences · {res.already_approved} were already approved · {res.suggested || 0} possible matches suggested</p>}
    </div>
  );
};

export default function PhonePeCheck() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  const load = useCallback(async () => {
    try { setD((await api.get("/fees/phonepe/status")).data); } catch (e) { err(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const approveGroup = async (x) => {
    if (!window.confirm(`Approve ${x.students?.length || 1} payment(s) on UTR ${x.utr}?\n${(x.students || []).join("\n")}`)) return;
    setBusy(x.utr);
    try {
      for (const cid of x.collection_ids || []) {
        try { await api.post(`/fees/collections/${cid}/approve`, { note: `Approved via PhonePe check (UTR ${x.utr})` }); }
        catch (e) { if (e.response?.status !== 409) throw e; }
      }
      await api.post(`/fees/phonepe/credits/${x.utr}/approved`);
      toast.success("Approved"); load();
    } catch (e) { err(e); } finally { setBusy(""); }
  };
  const notFee = async (x) => {
    setBusy(x.utr);
    try { await api.post(`/fees/phonepe/credits/${x.utr}/not-fee`, { note: "Not a fee" }); load(); } catch (e) { err(e); } finally { setBusy(""); }
  };
  const act = async (key, fn, ok) => { setBusy(key); try { const r = await fn(); toast.success(ok(r.data)); load(); } catch (e) { err(e); } finally { setBusy(""); } };
  const clearIt = (x) => {
    const note = window.prompt("Why is this OK? (saved with your name)", "Checked — amounts are correct");
    if (note === null) return;
    act(x.utr, () => api.post(`/fees/phonepe/credits/${x.utr}/clear`, { note }), () => "Cleared");
  };
  const linkTo = (x, ids) => act(x.utr, () => api.post(`/fees/phonepe/credits/${x.utr}/link`, { collection_ids: ids }), (r) => (r.status === "matched" ? "Linked (already approved)" : "Linked — now approve it above"));

  if (!d) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-blue-900" /></div>;
  return (
    <div className="space-y-4" data-testid="phonepe-check">
      <UploadBox onDone={load} />
      <div className="flex flex-wrap gap-2 items-center text-xs text-slate-500">
        {d.last_upload ? <span data-testid="phonepe-last">Last statement: {d.last_upload.period.from} to {d.last_upload.period.to} · uploaded {formatDateTime(d.last_upload.created_at)}</span> : <span>No statement uploaded yet.</span>}
        <Button size="sm" variant="outline" onClick={() => act("rematch", () => api.post("/fees/phonepe/rematch"), (r) => `${r.auto_approved} auto-approved · ${r.needs_approval} need approval`)} disabled={busy === "rematch"} data-testid="phonepe-rematch"><RefreshCw className="h-4 w-4 mr-1" /> Match again</Button>
        <Button size="sm" variant="outline" onClick={() => act("mail", () => api.post("/fees/daily-summary/preview"), (r) => (r.sent ? `Summary emailed to ${r.to}` : `Summary logged for ${r.to || "admin"} (email is in TEST mode)`))} disabled={busy === "mail"} data-testid="phonepe-summary-preview"><Mail className="h-4 w-4 mr-1" /> Send today's summary now</Button>
        <PastSummary />
      </div>
      <Section title="Found in PhonePe — needs your approval" tone="text-amber-700" items={d.needs_approval} empty="Nothing waiting." testid="phonepe-needs-approval">
        {(x) => <Row key={x.utr} x={x} testid={`phonepe-na-${x.utr}`}><Button size="sm" className="bg-green-600 hover:bg-green-700" disabled={busy === x.utr} onClick={() => approveGroup(x)} data-testid={`phonepe-approve-${x.utr}`}>Approve</Button></Row>}
      </Section>
      <Section title="Amount differs — check, then correct in Pending Approvals or clear it" tone="text-red-700" items={d.mismatch} empty="No differences." testid="phonepe-mismatch">
        {(x) => <Row key={x.utr} x={x} testid={`phonepe-mm-${x.utr}`}><div className="flex flex-wrap gap-2 w-full sm:w-auto"><LinkBox x={x} onDone={load} /><Button size="sm" variant="outline" disabled={busy === x.utr} onClick={() => clearIt(x)} data-testid={`phonepe-clear-${x.utr}`}>I checked — clear</Button></div></Row>}
      </Section>
      <Section title="UPI entered in the app but not in this PhonePe statement" tone="text-amber-700" items={d.not_in_phonepe} empty="All UPI entries in these dates were found." testid="phonepe-not-found">
        {(c) => (
          <div key={c.id} className="rounded-lg border border-slate-100 bg-slate-50 p-2 text-sm" data-testid={`phonepe-nf-${c.id}`}>
            <p className="font-medium text-slate-800">{c.student_name} · Roll {c.roll_number} · {inr(c.amount)}</p>
            <p className="text-xs text-slate-500">{c.date} · by {c.teacher_name} · UTR {c.reference || "not entered"} — stays in Pending Approvals</p>
            {c.utr_used_by && <p className="text-xs font-semibold text-red-700" data-testid={`phonepe-nf-dup-${c.id}`}>This UTR was already used for {c.utr_used_by} — possible reused screenshot</p>}
          </div>
        )}
      </Section>
      <Section title="In PhonePe but not recorded in the app" tone="text-red-700" items={[...d.unrecorded].sort((a, b) => (b.suggest ? 1 : 0) - (a.suggest ? 1 : 0))} empty="Every PhonePe credit is recorded." testid="phonepe-unrecorded">
        {(x) => (
          <Row key={x.utr} x={x} testid={`phonepe-ur-${x.utr}`}>
            <div className="flex flex-col items-end gap-1">
              {x.suggest && <p className="text-xs text-blue-800 max-w-md text-right" data-testid={`phonepe-suggest-${x.utr}`}>Possible: {x.suggest.text}</p>}
              <div className="flex gap-2">
                {x.suggest && <Button size="sm" className="bg-blue-900 hover:bg-blue-950" disabled={busy === x.utr} onClick={() => linkTo(x, [x.suggest.collection_id])} data-testid={`phonepe-link-suggest-${x.utr}`}>Link this</Button>}
                <LinkBox x={x} onDone={load} />
                <Button size="sm" variant="outline" disabled={busy === x.utr} onClick={() => notFee(x)} data-testid={`phonepe-notfee-${x.utr}`}>Not a fee</Button>
              </div>
            </div>
          </Row>
        )}
      </Section>
      <ClearedList items={[...(d.not_fee || []), ...(d.cleared || [])]} onRestore={(x) => act(x.utr, () => api.post(`/fees/phonepe/credits/${x.utr}/restore`), () => "Restored — press Match again")}
        onRestoreAll={() => act("restoreall", () => api.post("/fees/phonepe/restore-all"), (r) => `${r.restored} restored · ${r.needs_approval} need approval · ${r.already_approved} already approved · ${r.suggested} suggested`)} busy={busy} />
    </div>
  );
}
